import { onMounted, onUnmounted, ref } from 'vue'

import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { useIntersectionObserver } from '@vueuse/core'
import { useRoute } from 'vue-router'

import type { Product } from '~/scopes/core/models/product'

export type Provider = 'attraqt' | 'algolia' | 'product page'

function toTrackingData(
  product: Product,
  provider: Provider,
  list: string,
  dealType: 'normal' = 'normal',
): Record<string, NonNullable<unknown>> {
  const data = {
    averageRate: product.reviewRating?.average,
    brand: product.brand,
    category: product.category,
    stock: product.availableStock,
    currency: product.price.currency,
    uuid: product.id,
    id: product.legacyId,
    listingId: product.listingId,
    numberTotalReviews: product.reviewRating?.count,
    price: product.price.amount,
    variant: product.grade.value,
    warrantyDuration: product.warrantyDuration,
    name: product.title,
    model: product.model,
    image: product.image,
    color: product.specifications.color,
    merchantId: product.sellerId,
    list,
    provider,
    dealType,
  }

  return Object.fromEntries(
    Object.entries(data).filter(
      (entry): entry is [string, string | number] =>
        entry[1] !== null && entry[1] !== undefined,
    ),
  )
}

export function usePageReference() {
  const { name, params, fullPath } = useRoute()
  const pageNameParam = params.pageName
  const pageName = Array.isArray(pageNameParam)
    ? pageNameParam.join(',')
    : pageNameParam
  const routeName = typeof name === 'symbol' ? name.description : name

  return pageName ?? routeName ?? fullPath
}

export function useImpressionTracking(ms = 1000) {
  const trackingData = new Map<Element, Record<string, NonNullable<unknown>>>()
  const bucket: Set<Record<string, NonNullable<unknown>>> = new Set()
  const targets = ref<HTMLElement[]>([])

  const { stop } = useIntersectionObserver(
    targets,
    (entries) => {
      entries.forEach((entry) => {
        const trackingDataItem = trackingData.get(entry.target)
        if (entry.isIntersecting && trackingDataItem) {
          bucket.add(trackingDataItem)
        }
      })
    },
    { threshold: 0.8 },
  )

  /**
   * Use is in the template as follow
   * :ref="(el) => setTrackingRef(data, el as HTMLElement)"
   *
   * @param data a serializable JSON object
   * @param el the HTML Element
   */
  const setTrackingRef = (
    product: Product,
    provider: Provider,
    list: string,
    el: HTMLElement | null,
  ) => {
    if (el) {
      trackingData.set(el, toTrackingData(product, provider, list))
      targets.value.push(el)
    }
  }

  const { trackProductImpressionBatched } = useTracking()

  onMounted(() => {
    setInterval(() => {
      if (bucket.size > 0) {
        trackProductImpressionBatched(Array.from(bucket))
        bucket.clear()
      }
    }, ms)
  })

  onUnmounted(() => {
    stop()
    trackingData.clear()
  })

  return { setTrackingRef }
}

export function useClickTracking() {
  const { trackProductClick: genericTrackProductClick } = useTracking()

  function trackProductClick(
    product: Product,
    provider: Provider,
    list: string,
  ) {
    genericTrackProductClick(toTrackingData(product, provider, list))
  }

  return { trackProductClick }
}
